<template>
    <div :id="datePickerId" :ref="datePickerId"
         class="relative-wrap" :class="{'date-range-disabled': disabled}">
        <!-- selected date range -->
        <div @click="toggle()"
             class="date-range-field"
             :class="{'selected-item-as-title font-1 no-button-limit': isTitleStyle,
                      'font-5': !isTitleStyle}">
            <span>{{ currentDate }}</span>
            <div class="small-triangle" v-once></div>
        </div>

        <div class="pop-up show-fade disappearImmediate"
             v-iron-source-click-outside="{
                    handler: 'closeDatePicker',
                    exclude: [datePickerId]
                 }"
             v-show="isOpen" :id="'datepickerPopup' + uniqueId" :class="placement">
            <!-- range types - days/weeks/months -->
            <div v-if="types.length > 1" class="type-picker">
                <span v-for="type in types" :key="type"
                      class="range-type font-5"
                      :class="{'selected-range-type font-4': rangeType === type}"
                      @click="changeType(type, true)">
                    {{ typeToName[type] }}
                </span>
            </div>
            <div :class="{'pop-up-content': types.length > 1}">
                <div class="inline-block date-picker-menu">
                    <!-- preset list -->
                    <div :id="'presets' + uniqueId" class="preset-dropdown">
                        <ul :id="'customDropDownList' + uniqueId" class="font-5">
                            <li v-for="uic in presets" :key="uic.id"
                                :class="{'selected-preset-container': selectedPreset === uic.id}">
                                <div :class="{'selected-preset':selectedPreset === uic.id}"
                                     @click="presetChanged(uic)"
                                     class="preset-element">
                                    {{ uic["name"] }}
                                </div>
                                <div v-show="uic.id === 'custom'" class="dropdown-rightTriangle"></div>
                            </li>
                        </ul>
                    </div>

                    <div class="type-message font-7">
                        {{ messagePerType[rangeType] }}
                    </div>
                </div>
                <!-- calendars -->
                <div class="calendar-wrap">
                    <div class="date-picker-calendar left-box">
                        <div v-for="(currMonth, index) in displayedMonthsKeys" :key="currMonth">
                            <CalendarMonth
                                    :navigation="getNavigation(index === 0,
                                                               index === displayedMonthsKeys.length - 1)"
                                    :year="currentMonths[currMonth].parsedMonth.year"
                                    :days="currentMonths[currMonth].parsedMonth.days"
                                    :weeks="currentMonths[currMonth].parsedMonth.weeks"
                                    :month="currentMonths[currMonth].parsedMonth.month"
                                    :id="uniqueId"
                                    :range="selectedRange"
                                    :type="rangeType"
                                    :allowedDays="allowedDays"
                                    @selectionChanged="selectionChanged"
                                    @navigation="navigationCallback"
                            ></CalendarMonth>
                        </div>

                        <!-- apply/cancel buttons -->
                        <div class="buttons-cover">
                            <div class="range">
                                <div v-show="selectedRange && selectedRange.fromStart && selectedRange.toEnd">
                                    <span class="inline-block space-right">
                                        <div class="font-4 top-date">
                                            {{ selectedRangeStart }}
                                        </div>
                                        <div class="from-to-sub-text">
                                            {{ selectedRangeStartSubText }}
                                        </div>
                                    </span>
                                    <IconBase width="11" height="11" iconName="arrowUpIcon"
                                              class="from-to-arrow"
                                              :iconColor="consts.COLORS.LIGHT_800"/>
                                    <span class="inline-block space-left">
                                        <div class="font-4 top-date">
                                            {{ selectedRangeEnd }}
                                        </div>
                                        <div class="from-to-sub-text">
                                            {{ selectedRangeEndSubText }}
                                        </div>
                                    </span>
                                </div>
                            </div>
                            <div class="error" v-show="displayError">{{ error }}</div>
                            <div class="buttons" v-once>
                                <button @click="apply()"
                                        :id="'button-ok' + uniqueId" class="primary">Apply</button>
                                <button type="button" :id="'button-cancel' + uniqueId" class="secondary"
                                        @click="toggle()">
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import mixpanelService from 'services/utils/mixpanel';
import moment from 'moment';
import ironSourceClickOutside from 'directives/IronSourceClickOutside';
import IconBase from 'components/IconBase.vue';
import consts from 'services/metaData/consts';
import CalendarMonth from './CalendarMonth.vue';
import dateServices from './DateServices';
import {
    presetsList,
    navigateNext,
    navigatePrev,
    formatError,
    mandatoryValuesError,
    messagePerType,
    typeToName
} from './consts';

export default {
    name: 'DateRange',
    components: { IconBase, CalendarMonth },
    directives: {
        ironSourceClickOutside
    },
    props: {
        range: { // the selected date
            type: Object,
            validator: (val) => val.from && val.to
        },
        numOfMonths: {
            type: Number,
            default: 3
        },
        types: {
            type: Array,
            default: () => ['date'],
            validator: (val) => !(val.reduce((acc, type) => acc || !['date', 'week', 'month'].includes(type), false))
        },
        isTitleStyle: {
            type: Boolean,
            default: false
        },
        includeToday: {
            type: Boolean,
            default: true
        },
        allowedDays: {
            type: Number,
            default: null
        },
        datePresetList: {
            type: String,
            default: 'date',
            validator: (val) => presetsList[val]
        },
        regex: {
            type: RegExp,
            default: () => /^(1[0-2]|0?[1-9])\/[\d]{1,2}\/[\d]{4}$/
        },
        dateFormat: {
            type: String,
            default: 'MM/DD/YYYY'
            // only supports format recognized by date.parse().
            // If you want a different format from default - must provide validation regex,
        },
        uniqueId: String,
        disabled: {
            type: Boolean,
            default: false
        },
        initialType: {
            type: String,
            validator: (val) => [null, 'date', 'week', 'month'].includes(val),
            required: false
        }
    },
    data() {
        return {
            selectionStart: { started: false, date: null },
            initialized: false,
            textDates: {},
            currentMonths: {},
            displayedMonthsKeys: [],
            datePresets: null,
            selectedPreset: null,
            displayError: false,
            rangeType: null,
            isOpen: false,
            selectedRange: null,
            messagePerType,
            typeToName,
            placement: 'left',
            currentDate: null,
            error: null,
            presets: null,
            consts
        };
    },
    watch: {
        range(newVal, oldVal) {
            if (newVal && newVal !== oldVal) {
                if (this.initialized) {
                    this.determinePreset(newVal);
                    this.initRange(newVal);
                    this.setCurrentDate();
                } else {
                    this.init();
                }
            }
        }
    },
    computed: {
        selectedPresetName() {
            const selectedPreset = this.presets.find((preset) => preset.id === this.selectedPreset);
            return selectedPreset ? selectedPreset.name : '';
        },
        selectedRangeStart() {
            return this.selectedRange && this.getDateTextFormatted(this.selectedRange.fromStart);
        },
        selectedRangeStartSubText() {
            return this.selectedRange && this.getDateSubTextFormatted(this.selectedRange.fromStart);
        },
        selectedRangeEnd() {
            return this.selectedRange && this.getDateTextFormatted(this.selectedRange.toEnd);
        },
        selectedRangeEndSubText() {
            return this.selectedRange && this.getDateSubTextFormatted(this.selectedRange.toEnd);
        },
        datePickerId() {
            return `datepicker${this.uniqueId}`;
        }
    },
    mounted() {
        this.init();
    },
    methods: {
        getDateTextFormatted(rawDate) {
            let date = rawDate;
            switch (this.rangeType) {
            case 'date':
                date = moment(date).format('MMM D, YYYY');
                break;
            case 'week':
                date = `Week ${moment(date).format('w, YYYY')}`;
                break;
            case 'month':
                date = moment(date).format('MMMM YYYY');
                break;
            default:
                break;
            }
            return date;
        },
        getDateSubTextFormatted(rawDate) {
            let date = rawDate;
            switch (this.rangeType) {
            case 'date':
                date = moment(date)
                    .format('dddd');
                break;
            case 'week':
                date = `Week ${moment(date)
                    .format('MMM D, YYYY')}`;
                break;
            case 'month':
                date = moment(date)
                    .format('MMM D, YYYY');
                break;
            default:
                break;
            }
            return date;
        },
        // create a month object
        createMonth(month, year) {
            return dateServices.createMonth(month, year, this.includeToday);
        },
        initMonths() { // init the initial month display
            let now = dateServices.getCurrentTimeUTC();
            if (this.range && this.range.to) {
                now = this.range.to;
            }

            if (!this.currentMonths[`${now.getFullYear().toString()}_${now.getMonth().toString()}`]) {
                this.currentMonths = {};
                this.displayedMonthsKeys = [];

                for (let i = 0; i < this.numOfMonths; i++) {
                    const lastMonth = new Date(now);
                    lastMonth.setDate(1);
                    lastMonth.setMonth(now.getMonth() - i);
                    const key = `${lastMonth.getFullYear().toString()}_${lastMonth.getMonth().toString()}`;
                    this.currentMonths[key] = {
                        parsedMonth: this.createMonth(lastMonth.getMonth(), lastMonth.getFullYear()),
                        order: i
                    };
                    this.displayedMonthsKeys.unshift(key);
                }
            }
        },
        changeType(type, resetDate) {
            this.rangeType = type;

            // get relevant presets
            let presetType = type;
            if (type === 'date') {
                presetType = this.datePresetList;
            }
            this.presets = presetsList[presetType];

            if (resetDate) {
                // reset selection
                this.selectedRange = {
                    fromStart: null, fromEnd: null, toStart: null, toEnd: null
                };
                this.textDates.from = '';
                this.textDates.to = '';
                this.selectionStart = { started: false, date: null };
                this.selectedPreset = 'custom';
            }

            this.$emit('rangeTypeChanged', this.rangeType);
        },
        presetChanged(selectedObj) {
            this.selectedPreset = selectedObj.id;
            this.initRange(this.datePresets[selectedObj.id].dates());
            if (!this.customSelected) {
                this.apply();
            }
        },
        setCurrentDate() {
            if (this.selectedPreset === 'custom') {
                this.currentDate = dateServices.getDateRangeString(this.selectedRange);
            } else {
                this.currentDate = this.selectedPresetName;
            }
            this.$emit('currentDateChanged', this.currentDate);
        },
        apply() {
            if (this.selectedRange.fromStart == null || this.selectedRange.toEnd == null) {
                this.displayError = true;
                this.error = mandatoryValuesError;
                return;
            }

            const today = dateServices.getCurrentTimeUTC();
            if (this.selectedRange.toEnd > today) {
                this.selectedRange.toEnd = today;
            }

            this.setCurrentDate();
            this.customSelected = this.selectedPreset === 'custom';
            this.savedPreset = this.selectedPreset;
            this.$emit('rangeChanged', { from: this.selectedRange.fromStart, to: this.selectedRange.toEnd });

            // Track MP/GA event - type changed
            if (this.typeOnOpen !== this.rangeType) {
                mixpanelService.trackEvent('Platform',
                    'dateRangeTypeChanged',
                    this.$route,
                    { Mode: this.rangeType });
            }
            if (this.customSelected) {
                mixpanelService.trackEvent('Platform', 'dateRangeCustomSelected', { Mode: this.rangeType });
            }

            this.toggle();
        },
        getNavigation(isFirst, isLast) {
            if (this.numOfMonths === 1) {
                return 'all';
            }
            if (isFirst) {
                return navigatePrev;
            }
            if (isLast) {
                return navigateNext;
            }
            return null;
        },
        navigationCallback(direction) {
            let newMonth = null;
            let newYear = null;
            const maxOrder = this.numOfMonths - 1;
            let stringKey = '';
            let lastMonth = null;
            let firstMonth = null;

            switch (direction) {
            case navigateNext:
                Object.keys(this.currentMonths).forEach((key) => {
                    const displayedMonth = this.currentMonths[key];
                    if (displayedMonth.order === 0) {
                        lastMonth = displayedMonth;
                    }

                    if (displayedMonth.order === maxOrder) {
                        this.displayedMonthsKeys.splice(this.displayedMonthsKeys.indexOf(key), 1);
                        delete this.currentMonths[key];
                    } else {
                        displayedMonth.order += 1;
                    }
                });
                newMonth = lastMonth.parsedMonth.month + 1;
                newYear = lastMonth.parsedMonth.year;
                if (newMonth > 11) {
                    newMonth = 0;
                    newYear += 1;
                }
                stringKey = `${newYear.toString()}_${newMonth.toString()}`;
                this.currentMonths[stringKey] = { parsedMonth: this.createMonth(newMonth, newYear), order: 0 };
                this.displayedMonthsKeys.push(stringKey);
                break;
            case navigatePrev:
                Object.keys(this.currentMonths).forEach((key) => {
                    const displayedMonth = this.currentMonths[key];
                    if (displayedMonth.order === maxOrder) {
                        firstMonth = displayedMonth;
                    }

                    if (displayedMonth.order === 0) {
                        this.displayedMonthsKeys.splice(this.displayedMonthsKeys.indexOf(key), 1);
                        delete this.currentMonths[key];
                    } else {
                        displayedMonth.order -= 1;
                    }
                });
                newMonth = firstMonth.parsedMonth.month - 1;
                newYear = firstMonth.parsedMonth.year;
                if (newMonth < 0) {
                    newMonth = 11;
                    newYear -= 1;
                }
                stringKey = `${newYear.toString()}_${newMonth.toString()}`;
                this.currentMonths[stringKey] = { parsedMonth: this.createMonth(newMonth, newYear), order: maxOrder };
                this.displayedMonthsKeys.unshift(stringKey);
                break;
            default:
                break;
            }
        },
        getSelectedRangeByType(dateRange) {
            let formattedDateRange;
            switch (this.rangeType) {
            case 'week':
                formattedDateRange = {
                    fromStart: dateServices.getFirstDayOfWeek(dateRange.from),
                    fromEnd: dateServices.getLastDayOfWeek(dateRange.from),
                    toStart: dateServices.getFirstDayOfWeek(dateRange.to),
                    toEnd: dateServices.getLastDayOfWeek(dateRange.to)
                };
                break;
            case 'month':
                formattedDateRange = {
                    fromStart: new Date(dateRange.from.getFullYear(), dateRange.from.getMonth(), 1),
                    fromEnd: dateServices.getLastDayOfMonth(dateRange.from),
                    toStart: new Date(dateRange.to.getFullYear(), dateRange.to.getMonth(), 1),
                    toEnd: dateServices.getLastDayOfMonth(dateRange.to)
                };
                break;
            case 'date':
            default:
                formattedDateRange = {
                    fromStart: dateRange.from, fromEnd: dateRange.from, toStart: dateRange.to, toEnd: dateRange.to
                };
                break;
            }

            return formattedDateRange;
        },
        initRange(newValue) {
            let newRange = newValue;
            if (!newValue || (!newValue.from && !newValue.to)) {
                newRange = this.datePresets[this.presets[0]];
            }

            // validate new range before changing to it
            this.displayError = false;
            if (!newRange.from || !newRange.to || !(newRange.from instanceof Date) || !(newRange.to instanceof Date)) {
                this.error = formatError;
                this.displayError = true;
                return false;
            }
            if (newRange.from > newRange.to) {
                this.error = '"From" date can\'t be bigger than "To" date';
                this.displayError = true;
                return false;
            }

            this.selectedRange = this.getSelectedRangeByType(newRange);
            this.textDates.from = moment(this.selectedRange.fromStart).format(this.dateFormat);
            this.textDates.to = moment(this.selectedRange.toEnd).format(this.dateFormat);
            return true;
        },
        compareDates(dateA, dateB) {
            return dateA.getUTCFullYear() === dateB.getUTCFullYear()
                && dateA.getUTCMonth() === dateB.getUTCMonth()
                && dateA.getUTCDate() === dateB.getUTCDate();
        },
        determinePreset(range) {
            let found = false;
            const currentRange = { from: new Date(range.from), to: new Date(range.to) };
            currentRange.from.setHours(0, 0, 0, 0);
            currentRange.to.setHours(0, 0, 0, 0);
            const availablePresetKeys = this.presets.map((item) => item.id);
            Object.keys(this.datePresets).forEach((key) => {
                const dateRange = this.datePresets[key];
                if (key !== 'custom' && !found && availablePresetKeys.indexOf(key) !== -1) {
                    const presetRange = dateRange.dates();
                    presetRange.from.setHours(0, 0, 0, 0);
                    presetRange.to.setHours(0, 0, 0, 0);
                    if (this.compareDates(presetRange.from, currentRange.from)
                        && this.compareDates(presetRange.to, currentRange.to)) {
                        this.selectedPreset = key;
                        found = true;
                    }
                }
            });

            if (found) {
                const presetItem = this.presets.find((preset) => preset.id === this.selectedPreset);
                this.$emit('dateRangePresetNameChanged', presetItem.name);
            } else {
                this.$emit('dateRangePresetNameChanged', null);
                this.selectedPreset = 'custom';
            }

            this.savedPreset = this.selectedPreset;
        },
        selectionChanged(day, month, year) {
            // starting selection
            if (!this.selectionStart.started) {
                this.selectedPreset = 'custom';
                this.selectionStart.started = true;
                this.selectionStart.date = new Date(year, month, day.value);

                this.initRange({ from: this.selectionStart.date, to: this.selectionStart.date });
            } else { // initial date already chosen
                const endDate = new Date(year, month, day.value);
                const startDate = this.selectionStart.date;
                this.selectionStart.started = false;
                this.selectionStart.date = null;

                // if the current date is earlier than the initial date, start selection again with the new date
                if (endDate < startDate) {
                    this.selectionChanged(day, month, year);
                } else { // end date selected, initialize the selected range
                    this.initRange({ from: startDate, to: endDate });
                }
            }
        },
        checkPlacement() {
            const position = this.$refs[this.datePickerId].offsetLeft;

            if ((window.innerWidth - position) > 930) {
                return 'left';
            }
            return 'right';
        },
        toggle() {
            this.isOpen = !this.isOpen;

            // just opened
            if (this.isOpen) {
                this.placement = this.checkPlacement();
                this.typeOnOpen = this.rangeType;
            } else { // just closed
                this.initRange(this.range);
                this.initMonths();

                this.selectedPreset = this.savedPreset;
                this.customSelected = this.selectedPreset === 'custom';
            }
        },
        closeDatePicker() {
            if (this.isOpen) {
                this.toggle();
            }
        },
        initPresets() {
            this.datePresets = dateServices.getPresetsFunctions(this.includeToday);

            // add custom option
            this.datePresets.custom = {
                dates: () => this.range
            };
        },
        init() {
            if (!this.range) return;
            this.initialized = true;
            this.changeType(this.initialType || this.types[0]);
            this.initPresets();
            this.determinePreset(this.range);
            this.initRange(this.range);
            this.initMonths();
            this.setCurrentDate();
        }
    }
};
</script>

<style scoped lang="scss">
    @import "~styles/styleGuide";
    @import "~styles/buttons";

    $calendarWidth: 674px;
    $menuWidth: 249px;
    $hoverDayBackgound: darken($primary-100, 10%);
    $singleDayWidth: 28px;
    $singleDayHeight: 28px;
    $daterangeHeightField: 30px;
    $daterangeItemHeight: 40px;
    $dateRangeDropdownPadding: 20px;

    .pop-up {
        width: $menuWidth + $calendarWidth + 1px;
        position: absolute;
        z-index: 15;
        top: 26px;
        box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.3);
        background-color: $white;
        border-radius: $borderRadius;
        left: 1px;

        .date-picker-menu {
            border-top: $border;
            width: $menuWidth;
            background: $white;
            vertical-align: top;
            //position: absolute;
            //z-index: 0;

            .preset-element {
                padding-left: $dateRangeDropdownPadding;
                height: $daterangeItemHeight;
                &:hover {
                    background: $light-100;
                }
            }

            .selected-preset{
                border-left: 4px solid $primaryColor;
                background: $light-100;
                font-weight: bold;
            }

            li {
                border-bottom: $border;
                cursor: pointer;
                &:last-child{
                    border-bottom:none;
                }
            }

            @media #{$mobile}{
                width: 100%;//$menuWidth;
            }
        }

        .left-box {
            left: -1px;
        }

        .date-picker-calendar {
            box-sizing: border-box;
            height: 100%;
            padding: 15px 5px 15px 12px;
            border-left: $border;
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
        }

        .calendars-wrapper {
            padding-left: 6px;
        }

        .preset-dropdown ul {
            list-style: none;
            margin: 0;
            padding: 0;
            line-height: $daterangeItemHeight;
            border-bottom: $border;
        }

        .buttons-cover {
            background: $light-100;
            height: 72px;
            padding: 20px;
            position: absolute;
            bottom: 0;
            width: 100%;
            right: -1px;
            box-sizing: border-box;
            border-top: $border;
            display: grid;
            grid-template-columns: auto 250px;
        }

        button{
            float: right;
            margin-left: 10px;
        }

        .buttons {
            display: inline-block;
            float: right;
            height: inherit;
        }

        .error {
            position: absolute;
            left: 270px;
            top: 22px;
        }

        .inline-block {
            display: inline-block;
        }

        @media #{$mobile}{
            width: 100%;//$menuWidth;
        }

        &.right {
            left: initial;
            right: 1px;
        }
    }

    .calendar-wrap {
        margin-left: -4px;
        width: $calendarWidth;
        height: 345px;
        position:relative;
        display: inline-block;
        border-top: $border;
        border-bottom-right-radius: $borderRadius;
        border-top-right-radius: $borderRadius;
        vertical-align: top;

        @media #{$mobile}{
            display: none;
        }
    }

    .type-picker{
        height: 60px;
        line-height: 60px;
        padding-left: 15px;
        box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.23);

        @media #{$mobile} {
            display: none;
        }
    }

    .range-type{
        color: $light-800;
        padding-left: 15px;
        padding-right: 15px;
        cursor: pointer;
    }

    .selected-range-type{
        color: $white;
        background-color: $selectedColor;
        border-radius: 20px;
        padding: 6px 15px;
        cursor: default;
    }

    @mixin smallTriangle{
        .small-triangle {
            right: -25px;
            &:after {
                border-top-color: currentColor;
            }
        }
    }

    .date-range-field {
        display: inline-block;
        width: 100%;
        height: $daterangeHeightField;
        border: $border;
        @include inputGradient;
        overflow: hidden;
        position: relative;
        cursor: pointer;
        border-radius: 3px;

        &.selected-item-as-title {
            left:0;
            border: none;
            background-image: none;
            max-width: 100%;
            height: 22px;
            color: $primaryColor;
            overflow: visible;
            @include smallTriangle;
            span {
                padding-left: 12px;
            }
        }

        &.no-button-limit{
            overflow: visible;
            span{
                width: 100%;
                line-height: 22px;
            }
        }
    }

    .date-range-field span {
        line-height: $daterangeHeightField - 2px;
        padding-left: 15px;
        overflow: hidden;
        text-overflow: ellipsis;
        display: inline-block;
        width: calc(100% - 26px);
        white-space: nowrap;
        position: relative;
    }

    .date-range-disabled{
        .date-range-field {
            cursor: default;
            color: $disabledText;
            pointer-events: none;
        }
        .small-triangle:after{ // show the error with disabled color
            border-top-color: $disabledText;
        }
    }
    .from-to-arrow{
        top: -6px;
        position: relative;
        transform: rotate(90deg);
    }
    .from-to-sub-text{
        @include font(7);
        color: $light-800;
        line-height: 24px;
    }

    .pop-up-content{
        margin-top: 1px;

        @media #{$mobile} {
            margin-top: 0;
        }
    }
    .type-message{
        color: $light-800;
        padding: 10px 20px;
        line-height: 1.5;

        @media #{$mobile} {
            display: none;
        }
    }

    .relative-wrap {
        position: relative;
    }

    .space-right {
        margin-right: 13px;
    }

    .space-left {
        margin-left: 9px;
    }
    .top-date {
        line-height: 13px;
    }
</style>
